import {
  applySnapshot,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { GetPresignedDownloadURLForEmailCycleRecipientsRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getPresignedDownloadUrlForEmailCycleRecipientsRPC";
import { getAPIClient } from "../../networking/APIClient";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import { useGetPresignedDownloadURLForEmailCycleRecipientsRPCClientImpl } from "../rpcs/RPC";
import { downloadFile } from "../../utils/FileDownloadUtils";
import { getDelimitedFileName } from "@pulse/shared-components";

const getRecipientsFileName = (scheduleName: string): string => {
  const trimmedScheduleName = scheduleName.trim();
  return `${getDelimitedFileName(trimmedScheduleName)}.csv`;
};

const getInitialState = (): SnapshotIn<
  typeof DownloadEmailCycleRecipientsStore
> => ({
  isDownloadingRecipients: false,
  getPresignedDownloadURLForEmailCycleRecipientsRPCError: null,
  hasUnexpectedDownloadError: false,
});

export const DownloadEmailCycleRecipientsStore = types
  .model("DownloadEmailCycleRecipientsStore", {
    isDownloadingRecipients: types.boolean,
    getPresignedDownloadURLForEmailCycleRecipientsRPCError: types.maybeNull(
      types.enumeration(
        "GetPresignedDownloadURLForEmailCycleRecipientsRPCError",
        Object.values(
          GetPresignedDownloadURLForEmailCycleRecipientsRPC.RPCError,
        ),
      ),
    ),
    hasUnexpectedDownloadError: types.boolean,
  })
  .actions((store) => ({
    downloadEmailCycleRecipients: flow(function* (
      scheduleId: string,
      scheduleName: string,
    ) {
      if (store.isDownloadingRecipients) return;

      store.isDownloadingRecipients = true;
      store.getPresignedDownloadURLForEmailCycleRecipientsRPCError = null;
      store.hasUnexpectedDownloadError = false;

      try {
        const apiClient = getAPIClient(store);
        const request =
          new GetPresignedDownloadURLForEmailCycleRecipientsRPC.Request(
            new LeoUUID(scheduleId),
          );

        const result: LeoRPCResult<
          GetPresignedDownloadURLForEmailCycleRecipientsRPC.Response,
          GetPresignedDownloadURLForEmailCycleRecipientsRPC.Errors.Errors
        > =
          yield useGetPresignedDownloadURLForEmailCycleRecipientsRPCClientImpl(
            apiClient,
          ).execute(request);

        if ("response" in result) {
          yield downloadFile(
            result.response.documentURL.href,
            getRecipientsFileName(scheduleName),
          );
          return;
        }

        switch (result.error.code) {
          case GetPresignedDownloadURLForEmailCycleRecipientsRPC.RPCError
            .InvalidScheduleId:
            store.getPresignedDownloadURLForEmailCycleRecipientsRPCError =
              result.error.code;
            break;
          default:
            result.error.code satisfies never;
        }
      } catch {
        store.hasUnexpectedDownloadError = true;
      } finally {
        store.isDownloadingRecipients = false;
      }
    }),
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createDownloadEmailCycleRecipientsStore = (): Instance<
  typeof DownloadEmailCycleRecipientsStore
> => DownloadEmailCycleRecipientsStore.create(getInitialState());
